import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';

import { CAMPAIGNS_TYPES } from 'const';
import { IValidator } from 'interfaces/validator';
import {
	ICreateAdvertisingCampaignRequest,
	createAdvertisingCampaign,
} from 'services/advertisingCampaigns';
import {
	IYCompanyResponse,
	yandexAddClient,
	yandexCompanySearch,
} from 'services/yandex';
import { useDispatch, useSelector } from 'store';
import {
	refreshCompanyData as refreshCompanyDataAction,
	selectActiveCompany,
	selectCompanyProduct,
	selectCompanyProductSubclient,
} from 'store/companiesReducer';
import {
	selectActiveProductOffer,
	selectActiveProductSlug,
} from 'store/productsReducer';
import { updateCompany } from 'services/companies';
import { validateValue, validateForm } from 'utils/validation';
import Button from 'components/Button';
import Card from 'components/Card';
import Input from 'components/Input';
import InputPrefix from 'components/Input/InputPrefix';
import Radio from 'components/Input/Radio';
import Select from 'components/Input/Select';
import { IOption } from 'components/Input/Select/types';
import ModalConfirm from 'components/Modals/ModalConfirm';
import ModalSubclientLogin from 'components/Modals/ModalSubclientLogin';
import ModalInfoSubclientLoginSuccess from 'components/Modals/ModalInfoSubclientLoginSuccess';
import classes from './advertisingCampaignCreate.module.scss';

const AdvertisingCampaignCreate: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const activeProductOffer = useSelector(selectActiveProductOffer);
	const activeProductSlug = useSelector(selectActiveProductSlug);
	const activeCompany = useSelector(selectActiveCompany);
	const companyPartner = useSelector(selectCompanyProduct);
	const companySubclient = useSelector(selectCompanyProductSubclient);
	const [selectCompanyOptions, setSelectCompanyOptions] = useState<IOption[]>(
		[]
	);
	const [selectedCompany, setSelectedCompany] = useState<IOption | null>(
		null
	);
	const [campaignData, setCampaignData] = useState({
		companyName: '',
		campaignName: '',
		type: CAMPAIGNS_TYPES.SUBSCRIPTION,
		url: '',
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({
		companyName: '',
		campaignName: '',
		url: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingCompany, setIsLoadingCompany] = useState(false);
	const [modalOpen, setModalOpen] = useState('');
	const [subclientPassword, setSubclientPassword] = useState('');
	const [loginError, setLoginError] = useState('');

	const advertisingTypesOptions = [
		{
			value: CAMPAIGNS_TYPES.SUBSCRIPTION,
			label: 'Рекламная подписка',
			description:
				'Сервис сам создаст рекламные объявления на основе вашей информации об организации и запустит их в интернете. Реклама будет показываться в рекомендациях тем, кто ищет похожие организации, товары или услуги. Объявления появятся сразу на нескольких площадках Яндекса: Поиске, Картах, Услугах, Дзене, Рекламной сети. Реклама будет вести клиентов на карточку вашей организации в Яндексе. Вы сможете следить за статистикой просмотров карточки и действий в ней.',
			disabled: selectedCompany?.isOnline,
			disabledText: selectedCompany?.isOnline
				? '(Недоступно для онлайн организаций)'
				: '',
		},
		{
			value: CAMPAIGNS_TYPES.WEB,
			label: 'Рекламировать сайт или профиль в социальной сети',
			description:
				'Рекламные объявления приведут пользователей к вам на сайт, в профиль или на пост в соцсети. Подходит, если продаёте услуги и товары онлайн, хотите увеличить аудиторию в соцсети или охват поста',
		},
	];
	const isWebType = campaignData.type === CAMPAIGNS_TYPES.WEB;

	const validateFieldValue = ({ type, value }: IValidator): void => {
		const isRequired =
			type !== 'url' || campaignData.type === CAMPAIGNS_TYPES.WEB;
		const error = validateValue({ type, value, isRequired });
		setErrors({ ...errors, [type]: error });
	};

	const handleChangeCompanyName = async (
		searchText: string
	): Promise<IOption[]> => {
		setIsLoadingCompany(true);
		try {
			const yandexCompaniesData = await yandexCompanySearch(searchText);
			const yandexCompanies = yandexCompaniesData.data.companies;
			const newOptions = yandexCompanies.map(
				(company: IYCompanyResponse) => ({
					value: `${company?.id}`,
					label: `${company?.name} - ${company?.address}`,
					...company,
				})
			);
			setSelectCompanyOptions(newOptions);
			setIsLoadingCompany(false);
			return newOptions;
		} catch (e) {
			setIsLoadingCompany(false);
			setErrors({ ...errors, companyName: 'Не удалось найти компанию' });
			return [];
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleChangeCompanyNameDebounced = useCallback(
		debounce(handleChangeCompanyName, 500),
		[]
	);

	const handleSelectCompany = (option: IOption | null) => {
		setSelectedCompany(option);
		const value = option?.name || '';
		setCampaignData({
			...campaignData,
			companyName: value,
			type: option?.isOnline
				? CAMPAIGNS_TYPES.WEB
				: CAMPAIGNS_TYPES.SUBSCRIPTION,
		});
		validateFieldValue({ type: 'companyName', value });
	};

	const handleSetCompany = async () => {
		const companyId = companyPartner?.id;
		const companyName = companyPartner?.name || activeCompany?.name || '';
		const companyAddress =
			companyPartner?.address ||
			`${activeCompany?.country}, ${activeCompany?.region}, ${activeCompany?.address}` ||
			'';
		const searchCompanyText = `${companyName} ${companyAddress}`;
		const options = await handleChangeCompanyName(searchCompanyText);
		if (companyId) {
			const newSelectedOption =
				options.find((option) => option.value === `${companyId}`) ||
				null;
			handleSelectCompany(newSelectedOption);
		}
	};

	useEffect(() => {
		if (activeCompany) {
			handleSetCompany();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCompany]);

	const handleChange =
		(type: string) =>
		(value: string): void => {
			setCampaignData({ ...campaignData, [type]: value });
			validateFieldValue({ type, value });
		};

	const handleChangeType = (value: string): void => {
		setCampaignData({
			...campaignData,
			type: value,
		});
	};

	const refreshCompanyData = async (): Promise<void> => {
		try {
			await dispatch(refreshCompanyDataAction()).unwrap();
		} catch (e) {
			console.log(e);
		}
	};

	const handleCreateCampaign = async () => {
		setIsLoading(true);
		try {
			const activeCompanyId = activeCompany?.id;
			if (
				activeCompanyId &&
				(`${companyPartner?.id}` !== `${selectedCompany?.value}` ||
					`${companyPartner?.countryGeoId}` !==
						`${selectedCompany?.country?.geoId}`)
			) {
				await updateCompany(activeCompany.id, {
					partners: {
						[activeProductSlug]: {
							id: Number(selectedCompany?.id),
							address: selectedCompany?.address || '',
							chainId: selectedCompany?.isChain
								? Number(selectedCompany?.id)
								: 0,
							name: selectedCompany?.name,
							countryGeoId: Number(
								selectedCompany?.country.geoId
							),
						},
					},
				});
				await refreshCompanyData();
			}
			const requestData: ICreateAdvertisingCampaignRequest = {
				slug: activeProductSlug,
				payload: {
					company_id: Number(activeCompanyId),
					subclient_id: Number(companySubclient?.id),
					name: campaignData.campaignName || '',
					offer_id: activeProductOffer.offerId,
					asr_balance_dict_id: activeProductOffer.asrBalanceDictID,
					options: {
						type: campaignData.type,
					},
				},
			};
			if (isWebType) {
				requestData.payload.options.url = `https://${campaignData.url}`;
			}
			const { data: newCampaign } = await createAdvertisingCampaign(
				requestData
			);
			navigate(`../${newCampaign.id}/payment`);
		} catch (e) {
			setIsLoading(false);
		}
		setIsLoading(false);
	};

	const handleSubmit = (): void => {
		if (isLoading) return;

		const requiredFields = ['companyName', 'campaignName'];
		const formData: { [key: string]: string } = {
			companyName: campaignData.companyName,
			campaignName: campaignData.campaignName,
		};
		if (isWebType) {
			requiredFields.push('url');
			formData.url = campaignData.url;
		}
		const { isValid, formErrors } = validateForm({
			formData,
			requiredFields,
		});
		if (!isValid) {
			return setErrors(formErrors);
		}

		if (activeProductOffer.isPromoCodeAvailable) {
			return setModalOpen('promocode');
		}

		if (!companySubclient) {
			return setModalOpen('login');
		}

		handleCreateCampaign();
	};

	const handleCloseModal = (): void => {
		setModalOpen('');
	};

	const handleSubclientLogin = async (login: string): Promise<void> => {
		try {
			setLoginError('');
			const { data } = await yandexAddClient({
				login,
				company_id: activeCompany?.id || '',
			});
			// TODO: change when handle yandex errors
			if (
				data?.message === 'Не удалось создать клиента' ||
				data?.status === 'error'
			) {
				return setLoginError(
					data?.status === 'error'
						? 'Этот логин уже используется'
						: data?.status || ''
				);
			}
			setSubclientPassword(data.Password);
			await refreshCompanyData();
			setModalOpen('loginSuccess');
			// handleSubmit();
		} catch (e) {
			console.log(e);
		}
	};

	const handleLoginConfirm = () => {
		handleCloseModal();
		handleCreateCampaign();
	};

	const handlePromoConfirm = () => {
		if (!companySubclient) {
			return setModalOpen('login');
		}

		handleCreateCampaign();
	};

	const header = (
		<div className={classes.header}>
			<div className={classes.title}>Создание рекламной кампании</div>
		</div>
	);

	const companyForm = (
		<Card
			className={classes.card}
			title="Выберите организацию для рекламной кампании"
		>
			<Select
				className={classes.input}
				value={selectedCompany}
				options={selectCompanyOptions}
				onSelect={handleSelectCompany}
				onChange={handleChangeCompanyNameDebounced}
				placeholder="Название организации"
				error={errors.companyName}
				isSearch
				isLoading={isLoadingCompany}
			/>
			<div className={classes.selectCompanyWarn}>
				Если Вы не нашли свою организацию в списке, добавьте ее в&nbsp;
				<a
					href="https://yandex.ru/sprav/main"
					target="_blank"
					rel="noreferrer"
					className={classes.link}
				>
					Яндекс.Бизнес
				</a>
				. После добавления организации вернитесь к этому шагу и
				завершите создание рекламной кампании.
			</div>
		</Card>
	);

	const campaignForm = (
		<Card
			className={classes.card}
			title="Укажите название рекламной кампании"
		>
			<Input
				className={classes.input}
				value={campaignData.campaignName}
				onChange={handleChange('campaignName')}
				placeholder="Название рекламной кампании"
				error={errors.campaignName}
			/>
		</Card>
	);

	const advertisingTypeForm = (
		<Card className={classes.card} title="Выберите тип рекламы">
			<Radio
				className={classes.advertisingTypes}
				value={campaignData.type}
				options={advertisingTypesOptions}
				onChange={handleChangeType}
			/>
			{isWebType && (
				<InputPrefix
					className={cn(classes.input, classes.inputUrl)}
					value={campaignData.url}
					onChange={handleChange('url')}
					prefix="https://"
					placeholder="Адрес сайта"
					error={errors.url}
				/>
			)}
		</Card>
	);

	return (
		<div className={classes.advertisingCampaignCreate}>
			{header}
			{companyForm}
			{campaignForm}
			{advertisingTypeForm}
			<Button
				className={classes.button}
				text="Продолжить"
				onClick={handleSubmit}
				disabled={isLoading}
			/>
			<ModalSubclientLogin
				className={classes.modal}
				isOpen={modalOpen === 'login'}
				onClose={handleCloseModal}
				onSubmit={handleSubclientLogin}
				error={loginError}
				title="Создайте учетную запись"
				text="Для создания рекламной кампании вам необходимо создать учетную запись в Яндекс. Укажите логин, который вы хотите использовать"
			/>
			<ModalInfoSubclientLoginSuccess
				className={classes.successLoginModal}
				isOpen={modalOpen === 'loginSuccess'}
				onClose={handleLoginConfirm}
				title="Учетная запись создана"
				text="Используйте эти данные для входа в личный кабинет Яндекс.Бизнес. На всякий случай отправили эти данные вам на почту, которую вы указали при регистрации"
				login={companySubclient?.login}
				password={subclientPassword}
				passwordTooltip="Это временный пароль, который будет действителен для входа в учетную запись Яндекс Бизнес в течение 30 дней. Пароль необходимо изменить в личном кабинете сервиса Яндекс.Бизнес"
				buttonClassName={classes.infoButton}
			/>
			<ModalConfirm
				isOpen={modalOpen === 'promocode'}
				onClose={handleCloseModal}
				onConfirm={handlePromoConfirm}
				title="Добавляем промокод?"
				text="К первой кампании автоматически будет применен промокод на 5000 рублей. Продолжить создание компании?"
				confirmButtonText="Продолжить"
				cancelButtonText="Назад"
			/>
		</div>
	);
};

export default AdvertisingCampaignCreate;
